import { Pagination } from '@/constants/pagination';
import { useRootSelector } from '@/hooks/selector.hook';
import { useChatbot } from '@/services/chatbot.service';
import { useEffect, useState } from 'react';
import { IoCloseSharp, IoChatbubbleEllipsesOutline } from 'react-icons/io5';
import { MdDelete } from 'react-icons/md';
import LogoCollapsed from '@/assets/svg/logoCollapse.svg';

interface ChatbotSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  onNewChat?: () => void;
  onSelectChat?: (chatId: string) => void;
  currentSessionId?: string | null; // Add current session ID to check if deleting current chat
}

const ChatbotSidebar = ({
  isOpen,
  onClose,
  onNewChat,
  onSelectChat,
  currentSessionId,
}: ChatbotSidebarProps) => {
  const { getAllHistoryChatbot, deleteHistoryChat } = useChatbot();
  const { dataHistortChatbot } = useRootSelector((state) => state.chatbot);

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{
    visible: boolean;
    x: number;
    y: number;
    chatId: string | null;
  }>({ visible: false, x: 0, y: 0, chatId: null });

  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [chatToDelete, setChatToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false); // Loading state
  const handleNewChat = () => {
    if (onNewChat) {
      onNewChat();
    }
    // TODO: Call API to create new chat
  };

  const handleSelectChat = (chatId: string) => {
    if (onSelectChat) {
      onSelectChat(chatId);
    }
    // TODO: Call API to load chat history
    onClose();
  };

  // Handle right-click on chat item
  const handleContextMenu = (e: React.MouseEvent, chatId: string) => {
    e.preventDefault(); // Prevent default browser context menu
    e.stopPropagation(); // Stop event bubbling

    // Get the position of the clicked element
    const rect = e.currentTarget.getBoundingClientRect();

    setContextMenu({
      visible: true,
      x: rect.right + 10, // 10px to the right of the item
      y: rect.top, // Align with top of the item
      chatId,
    });
  };

  // Handle delete chat - Show confirmation modal
  const handleDeleteChat = () => {
    if (contextMenu.chatId) {
      setChatToDelete(contextMenu.chatId);
      setShowDeleteModal(true);
      setContextMenu({ visible: false, x: 0, y: 0, chatId: null });
    }
  };

  // Confirm delete
  const confirmDelete = async () => {
    if (chatToDelete) {
      setIsDeleting(true); // Start loading

      // Call API to delete chat
      const success = await deleteHistoryChat(chatToDelete);

      if (success) {
        // Always reset to new chat after deletion
        localStorage.removeItem('sessionId');

        // Trigger new chat callback
        if (onNewChat) {
          onNewChat();
        }
      }

      setIsDeleting(false); // Stop loading
      setShowDeleteModal(false);
      setChatToDelete(null);
    }
  };

  // Cancel delete
  const cancelDelete = () => {
    setShowDeleteModal(false);
    setChatToDelete(null);
  };

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (contextMenu.visible) {
        setContextMenu({ visible: false, x: 0, y: 0, chatId: null });
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [contextMenu.visible]);

  useEffect(() => {
    getAllHistoryChatbot({
      Page: Pagination.Page,
      Size: Pagination.Size,
    });
  }, [getAllHistoryChatbot]);

  return (
    <>
      {/* Overlay backdrop */}
      {isOpen && (
        <div
          className="absolute inset-0 bg-[#0000004D] rounded-[2.3rem] z-40 transition-opacity duration-300"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={`absolute top-0 left-0 h-full w-[26rem] bg-[#E6F0ED] dark:bg-[#161C1A] rounded-l-[2.3rem] z-50 transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Sidebar Header with Close Button */}
        <div className="pt-[2.6rem] [text-align:-webkit-right] pr-[1.2rem]">
          <IoCloseSharp
            className="size-[2.5rem] cursor-pointer text-[#1F1F1F] dark:text-[#FFFFFF]"
            onClick={onClose}
          />
        </div>

        {/* New Chat Button */}
        <div className="px-4 pb-4 border-b border-[#BBBBBB] dark:border-[#4A655C]">
          <button
            onClick={handleNewChat}
            className="w-full flex items-center gap-3 px-4 py-3 text-[#1F1F1F] dark:text-[#FFFFFF] hover:bg-[#D0E3DD] dark:hover:bg-[#1A1F1D] rounded-lg transition-colors duration-200"
          >
            <IoChatbubbleEllipsesOutline className="size-5" />
            <span className="text-[1.4rem] font-medium">New chat</span>
          </button>
        </div>

        {/* Chat History */}
        <div className="px-4 pt-4 overflow-y-auto max-h-[calc(100%-15rem)]">
          {dataHistortChatbot.map((chat) => {
            const isActive = chat.id === currentSessionId;

            return (
              <div
                key={chat.id}
                onClick={() => handleSelectChat(chat.id)}
                onContextMenu={(e) => handleContextMenu(e, chat.id)}
                className={`mb-2 px-4 py-3 text-[#1F1F1F] dark:text-[#FFFFFF] rounded-lg cursor-pointer transition-colors duration-200 ${
                  isActive
                    ? 'bg-[#B8D4CC] dark:bg-[#2A3F37] font-semibold'
                    : 'hover:bg-[#D0E3DD] dark:hover:bg-[#1A1F1D]'
                }`}
              >
                <p className="text-[1.3rem] truncate">{chat.id}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Context Menu - Fixed position next to chat item */}
      {contextMenu.visible && (
        <div
          className="fixed rounded-[1.4rem] py-2 z-[9999]"
          style={{
            backgroundColor: '#101010',
            border: '0.7px solid #9F9F9F',
            left: `${contextMenu.x}px`,
            top: `${contextMenu.y}px`,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={handleDeleteChat}
            className="flex items-center gap-3 px-4 py-2 w-full text-left text-[#FF4444] hover:bg-[#2A2A2A] transition-colors rounded-[1.4rem]"
          >
            <MdDelete className="size-5" />
            <span className="text-[1.3rem] font-medium">Remove</span>
          </button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 z-[9998] flex items-center justify-center"
            onClick={cancelDelete}
          >
            {/* Modal */}
            <div
              className="bg-[#202020] rounded-[2rem] p-[3rem] w-[45rem] flex flex-col items-center"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Logo */}
              <div className=" rounded-[1.6rem] py-[1.5rem]">
                <img src={LogoCollapsed} alt="Logo" className="w-[6rem] h-[6rem]" />
              </div>

              {/* Title */}
              <h2 className="text-[2rem] font-semibold text-white mb-[3rem]">Xóa đoạn Chat</h2>

              {/* Buttons */}
              <div className="flex gap-[1.6rem] w-full">
                {/* Cancel Button */}
                <button
                  onClick={cancelDelete}
                  disabled={isDeleting}
                  className="flex-1 py-[1.2rem] px-[2.4rem] bg-[#3A3A3A] hover:bg-[#4A4A4A] text-white text-[1.4rem] font-medium rounded-[1.2rem] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>

                {/* Delete Button */}
                <button
                  onClick={confirmDelete}
                  disabled={isDeleting}
                  className="flex-1 py-[1.2rem] px-[2.4rem] bg-[#FF4444] hover:bg-[#FF3333] text-white text-[1.4rem] font-medium rounded-[1.2rem] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-[0.8rem]"
                >
                  {isDeleting ? (
                    <>
                      <svg className="animate-spin h-[1.6rem] w-[1.6rem]" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Deleting...
                    </>
                  ) : (
                    'Delete'
                  )}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default ChatbotSidebar;
